import { ResponseError } from "@/openapi/generated/runtime";

export type StaffReviewConflict = {
  actedByEmail: string;
  actedAt: string | null;
  currentStatus: string | null;
};

function readString(
  record: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

export async function parseStaffReviewConflictFromResponse(
  response: Response,
): Promise<StaffReviewConflict | undefined> {
  const body = (await response.json().catch(() => null)) as Record<
    string,
    unknown
  > | null;
  if (!body) return undefined;

  return {
    actedByEmail:
      readString(body, "actedByEmail", "ActedByEmail", "reviewedByEmail", "ReviewedByEmail") ??
      "Someone",
    actedAt: readString(body, "actedAt", "ActedAt", "reviewedAt", "ReviewedAt"),
    currentStatus:
      readString(body, "currentStatus", "CurrentStatus", "status", "Status") ??
      null,
  };
}

export async function parseStaffReviewConflictFromError(
  error: unknown,
): Promise<StaffReviewConflict | undefined> {
  if (!(error instanceof ResponseError) || error.response.status !== 409) {
    return undefined;
  }
  return parseStaffReviewConflictFromResponse(error.response);
}

export class StaffReviewConflictError extends Error {
  readonly conflict: StaffReviewConflict;

  constructor(conflict: StaffReviewConflict) {
    super("Staff review conflict");
    this.name = "StaffReviewConflictError";
    this.conflict = conflict;
  }
}

export function formatStaffReviewConflictMessage(
  conflict: StaffReviewConflict,
  action: "approve" | "reject" | "cancel" | "generic" = "generic",
  subject: "request" | "withdrawal" = "request",
): string {
  const who = conflict.actedByEmail;
  const at = conflict.actedAt ? ` at ${formatConflictTime(conflict.actedAt)}` : "";
  const status = conflict.currentStatus;

  if (subject === "withdrawal") {
    if (status === "Dropped") {
      return `${who} already approved this withdrawal${at}.`;
    }
    if (status === "Enrolled") {
      return `${who} already cancelled this withdrawal${at}.`;
    }
    return `${who} already handled this withdrawal${at}.`;
  }

  if (status === "Enrolled" || status === "Approved") {
    return `${who} already approved this${at}.`;
  }
  if (status === "Rejected") {
    return `${who} already declined this${at}.`;
  }
  if (status === "Dropped" || status === "ScheduledDrop") {
    return `${who} already handled this withdrawal${at}.`;
  }
  if (status === "Cancelled") {
    return `${who} already cancelled this${at}.`;
  }
  if (status === "Deleted") {
    return `${who} already removed this${at}.`;
  }

  const verb =
    action === "approve"
      ? "approved"
      : action === "reject"
        ? "declined"
        : action === "cancel"
          ? "cancelled"
          : "handled";

  return `${who} already ${verb} this request${at}.`;
}

function formatConflictTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
